import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { jwtDecode } from 'jwt-decode'
import { AccessTokenRequest, getAccessToken } from '../api/auth'
import { DecodedAuth, hasRequiredRole, isValidAccessToken, load, remove, save } from '../utils/auth'
import { LOGIN_ROLES } from '../constants/roles'

export type AuthState = {
   token: string
   isAuthenticated: boolean
   isRefreshing: boolean
   isPristine: boolean
   hasError?: boolean
   statusText?: string
}

const initialState: AuthState = {
   token: '',
   isPristine: true,
   isRefreshing: false,
   isAuthenticated: false,
   hasError: false,
}

export const refreshToken = createAsyncThunk(
   'refreshToken',
   async (request: AccessTokenRequest, { rejectWithValue }) => {
      try {
         const response = await getAccessToken(request)
         if (!response) return rejectWithValue('Invalid email or password')
         const tokenDecoded = jwtDecode<DecodedAuth>(response.access_token)
         if (!hasRequiredRole(tokenDecoded, LOGIN_ROLES)) {
            return rejectWithValue('Unauthorized')
         }
         return response
      } catch {
         return rejectWithValue('Invalid email or password')
      }
   },
)

export const authSlice = createSlice({
   name: 'auth',
   initialState,
   reducers: {
      clear() {
         return initialState
      },
      logout(state) {
         state.token = ''
         state.isRefreshing = false
         state.isAuthenticated = false
         state.isPristine = false
         remove('_at')
      },
      verifyAuthThunk: {
         reducer: (state, action: PayloadAction<AuthState>) => {
            state.token = action.payload.token
            state.isAuthenticated = action.payload.isAuthenticated
            state.isPristine = action.payload.isPristine
         },
         prepare: () => {
            const cookieAuthToken = load('_at') || ''
            if (isValidAccessToken(cookieAuthToken)) {
               return {
                  payload: {
                     token: cookieAuthToken,
                     isAuthenticated: true,
                     isRefreshing: false,
                     hasError: false,
                     isPristine: false,
                  },
               }
            }

            return {
               payload: {
                  token: '',
                  isAuthenticated: false,
                  isRefreshing: false,
                  isPristine: false,
               },
            }
         },
      },
   },
   extraReducers(builder) {
      builder
         .addCase(refreshToken.pending, (state) => {
            state.isRefreshing = true
            state.hasError = false
            state.isPristine = false
         })
         .addCase(refreshToken.fulfilled, (state, action) => {
            save('_at', action.payload.access_token)
            state.token = action.payload.access_token
            state.isAuthenticated = true
            state.isRefreshing = false
            state.isPristine = false
            state.hasError = false
         })
         .addCase(refreshToken.rejected, (state, action) => {
            remove('_at')
            state.token = ''
            state.isRefreshing = false
            state.isAuthenticated = false
            state.isPristine = false
            state.hasError = true
            state.statusText = (action.payload as string) || 'Invalid email or password'
         })
   },
})
export const { clear, verifyAuthThunk, logout } = authSlice.actions

export default authSlice.reducer
